// Lightweight collapsible card used to declutter Child Profile. Shows a
// title + optional one-line summary when collapsed; expands to reveal the
// children on tap. Open state persists per `storageKey` so the therapist's
// preference survives reloads.
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { ChevronDown } from 'lucide-react';

export default function CollapsibleSection({
  icon,
  title,
  summary,
  defaultOpen = false,
  storageKey,
  testid,
  className = '',
  children,
}) {
  const [open, setOpen] = useState(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'true') return true;
      if (stored === 'false') return false;
    }
    return defaultOpen;
  });

  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, String(open));
  }, [open, storageKey]);

  return (
    <Card
      className={`bg-white rounded-3xl border border-stone-100 mb-6 overflow-hidden ${className}`}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-stone-50 transition-colors"
        data-testid={testid ? `${testid}-toggle` : undefined}
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {icon && (
            <div className="w-9 h-9 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-lg text-primary-900 truncate">{title}</h3>
            {!open && summary && (
              <p className="text-xs text-stone-500 truncate mt-0.5">{summary}</p>
            )}
          </div>
        </div>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-stone-400" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-stone-100">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
